import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { SpeakingLevelBadge } from "../UI/SpeakingLevelBadge.tsx";
import { BasicTable } from "../UI/Table/BasicTable.tsx";
import {
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline/index.js";
import { DropDown } from "../UI/DropDown/DropDown.tsx";
import { DropDownItem } from "../UI/DropDown/DropDownItem.tsx";
import { useCopyToClipboard } from "../UI/Feedback/useCopyToClipboard.ts";

type OneOnOneAssignmentPdfRow = {
  level: string;
  pdfUrl: string;
  userGroupId: string;
  userGroup?: { id: string; slug: string; name: string } | null;
  oneOnOneLessonType: string;
  assignmentNumber: number;
  businessEnglishObjective?: string | null;
};

type Props = {
  items: OneOnOneAssignmentPdfRow[];
  onEdit: (row: OneOnOneAssignmentPdfRow) => void;
  onDelete: (row: OneOnOneAssignmentPdfRow) => void;
};

const LESSON_TYPE_LABELS: Record<string, string> = {
  GeneralEnglish: "General English",
  BusinessEnglish: "Business English",
};

const LEVEL_ORDER: Record<string, number> = {
  Starters: 0,
  Explorers: 1,
  Navigators: 2,
  Pioneers: 3,
  Voyagers: 4,
};

function AssignmentTable({
  items,
  onEdit,
  onDelete,
}: {
  items: OneOnOneAssignmentPdfRow[];
  onEdit: (row: OneOnOneAssignmentPdfRow) => void;
  onDelete: (row: OneOnOneAssignmentPdfRow) => void;
}) {
  const copyToClipboard = useCopyToClipboard();
  const columnHelper = createColumnHelper<OneOnOneAssignmentPdfRow>();
  const columns = [
    columnHelper.accessor("level", {
      header: "Level",
      cell: (info) => <SpeakingLevelBadge level={info.getValue()} />,
    }),
    columnHelper.accessor("assignmentNumber", {
      header: "Assignment #",
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor("pdfUrl", {
      header: "Pdf Url",
      meta: { className: "hidden md:table-cell" },
      cell: (info) => (
        <div
          onClick={() => {
            copyToClipboard(info.getValue());
          }}
          className={
            "max-w-[300px] overflow-hidden overflow-ellipsis cursor-pointer"
          }
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      cell: (v) => (
        <div className={"flex justify-end"}>
          <DropDown
            activator={
              <EllipsisHorizontalIcon
                className={"text-slate-800 w-5 h-5 group-hover:text-white"}
              />
            }
          >
            <DropDownItem onClick={() => onEdit(v.row.original)}>
              <PencilSquareIcon className={"w-5 h-5 mr-2"} />
              Edit
            </DropDownItem>
            <DropDownItem onClick={() => onDelete(v.row.original)}>
              <TrashIcon className={"w-5 h-5 mr-2 text-red-500"} />
              <span className={"text-red-500"}>Delete</span>
            </DropDownItem>
          </DropDown>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    columns,
    data: items,
    getCoreRowModel: getCoreRowModel(),
    manualFiltering: true,
  });

  return <BasicTable table={table} />;
}

const ALL_BUSINESS_ENGLISH_OBJECTIVES = [
  "Presentazioni",
  "Negoziazioni",
  "Networking",
];

function LessonTypeSection({
  lessonType,
  items,
  onEdit,
  onDelete,
}: {
  lessonType: string;
  items: OneOnOneAssignmentPdfRow[];
  onEdit: (row: OneOnOneAssignmentPdfRow) => void;
  onDelete: (row: OneOnOneAssignmentPdfRow) => void;
}) {
  const isBusinessEnglish = lessonType === "BusinessEnglish";

  if (isBusinessEnglish) {
    const objectives = Array.from(
      new Set([
        ...ALL_BUSINESS_ENGLISH_OBJECTIVES,
        ...items
          .map((p) => p.businessEnglishObjective)
          .filter((o): o is string => !!o),
      ]),
    );

    return (
      <div className={"mt-4"}>
        <h4
          className={
            "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2"
          }
        >
          {LESSON_TYPE_LABELS[lessonType] ?? lessonType}
        </h4>
        <div className={"pl-4 border-l-2 border-slate-100"}>
          {objectives.map((obj) => {
            const objItems = items.filter(
              (p) => p.businessEnglishObjective === obj,
            );
            const sorted = [...objItems].sort(
              (a, b) =>
                (LEVEL_ORDER[a.level] ?? 99) - (LEVEL_ORDER[b.level] ?? 99) ||
                a.assignmentNumber - b.assignmentNumber,
            );
            return (
              <div key={obj} className={"mt-3"}>
                <h5 className={"text-xs font-semibold text-slate-400 mb-1"}>
                  {obj}
                </h5>
                {sorted.length === 0 ? (
                  <p className={"text-sm text-slate-400 italic"}>No PDFs yet</p>
                ) : (
                  <AssignmentTable
                    items={sorted}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const sorted = [...items].sort(
    (a, b) =>
      (LEVEL_ORDER[a.level] ?? 99) - (LEVEL_ORDER[b.level] ?? 99) ||
      a.assignmentNumber - b.assignmentNumber,
  );

  return (
    <div className={"mt-4"}>
      <h4
        className={
          "text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2"
        }
      >
        {LESSON_TYPE_LABELS[lessonType] ?? lessonType}
      </h4>
      {sorted.length === 0 ? (
        <p className={"text-sm text-slate-400 italic"}>No PDFs yet</p>
      ) : (
        <AssignmentTable items={sorted} onEdit={onEdit} onDelete={onDelete} />
      )}
    </div>
  );
}

function AudienceSection({
  label,
  items,
  onEdit,
  onDelete,
}: {
  label: string;
  items: OneOnOneAssignmentPdfRow[];
  onEdit: (row: OneOnOneAssignmentPdfRow) => void;
  onDelete: (row: OneOnOneAssignmentPdfRow) => void;
}) {
  const lessonTypes = Array.from(
    new Set(items.map((p) => p.oneOnOneLessonType)),
  ).sort();

  const allLessonTypes = ["GeneralEnglish", "BusinessEnglish"];
  const lessonTypesToShow = [
    ...allLessonTypes,
    ...lessonTypes.filter((t) => !allLessonTypes.includes(t)),
  ];

  return (
    <div className={"mt-4"}>
      <h3 className={"font-semibold text-base text-slate-600 mb-1"}>{label}</h3>
      <div className={"pl-4 border-l-2 border-slate-100"}>
        {lessonTypesToShow.map((lt) => (
          <LessonTypeSection
            key={lt}
            lessonType={lt}
            items={items.filter((p) => p.oneOnOneLessonType === lt)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

export function OneOnOneAssignmentPdfGroupedView({
  items,
  onEdit,
  onDelete,
}: Props) {
  const adultsItems = items.filter(
    (p) => !p.userGroup?.slug.toLowerCase().includes("teens"),
  );
  const teensItems = items.filter((p) =>
    p.userGroup?.slug.toLowerCase().includes("teens"),
  );

  return (
    <>
      <AudienceSection
        label="Adults"
        items={adultsItems}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      <AudienceSection
        label="Teens"
        items={teensItems}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </>
  );
}
